import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { FiUsers, FiX, FiCalendar, FiHash, FiShield, FiPlus, FiUserPlus, FiArrowRight, FiCheck, FiPhone, FiMail, FiBriefcase, FiClipboard } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { WO_TYPE_COLORS } from '../utils/constants';
import TeamOrgChart from '../components/TeamOrgChart';
import { AddUserModal } from './ManageUsers';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? ((parts[0][0] + parts[parts.length - 1][0])).toUpperCase() : parts[0][0].toUpperCase();
}

// Inline weekly working-hours editor used by the team profile modal.
// Persists via PATCH /api/users/:id/working-hours, which accepts supervisors
// acting on members of their own team.
function WorkingHoursEditor({ userId, initial, authFetch, showToast }) {
  const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const [hours, setHours] = useState(() => days.map(d => initial?.[`working_hours_${d}`] ?? (d === 'sat' || d === 'sun' ? 0 : 8)));
  const [saving, setSaving] = useState(false);
  const total = hours.reduce((s, h) => s + (parseFloat(h) || 0), 0);
  const save = async () => {
    setSaving(true);
    const body = {};
    days.forEach((d, i) => { body[d] = parseFloat(hours[i]) || 0; });
    const res = await authFetch(`/api/users/${userId}/working-hours`, { method: 'PATCH', body: JSON.stringify(body) });
    setSaving(false);
    if (res.ok) showToast('Working hours saved');
    else { const d = await res.json().catch(() => ({})); showToast(d.error || 'Failed to save hours', 'error'); }
  };
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Weekly Working Hours</div>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', background: 'rgba(37,99,235,0.08)', padding: '2px 8px', borderRadius: 4 }}>{total}h / week</span>
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        {days.map((d, i) => (
          <div key={d} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 3 }}>{labels[i]}</div>
            <input type="number" min="0" max="24" step="0.5" value={hours[i]}
              onChange={e => setHours(prev => { const n = [...prev]; n[i] = e.target.value; return n; })}
              style={{ width: '100%', padding: '6px 2px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, textAlign: 'center', background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
          </div>
        ))}
      </div>
      <button type="button" onClick={save} disabled={saving}
        style={{ marginTop: 8, padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
        {saving ? 'Saving…' : 'Save Hours'}
      </button>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function MyTeam() {
  const navigate = useNavigate();
  const { authFetch, user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultExpanded, setDefaultExpanded] = useState(new Set());
  const [profileUser, setProfileUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [myKpts, setMyKpts] = useState([]);
  const [actionMsg, setActionMsg] = useState('');
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [addMemberStep, setAddMemberStep] = useState('choose'); // choose | pickExisting | hours
  const [orphanUsers, setOrphanUsers] = useState([]);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [showOrphansView, setShowOrphansView] = useState(false);
  const [authMode, setAuthMode] = useState('local');
  const [pickedOrphan, setPickedOrphan] = useState(null);
  const [orphanHours, setOrphanHours] = useState({ mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 });
  const [orphanHoursTouched, setOrphanHoursTouched] = useState(false);
  const [orphanAssigning, setOrphanAssigning] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [nextEmployeeId, setNextEmployeeId] = useState('');
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const canManageUsers = user?.permissions?.includes('admin_panel') || myKpts.some(k => (k.permissions || '').split(',').includes('manage_team'));

  // Quietly refresh the teams list without flipping the page back into the
  // loading skeleton. Used after add/remove/move actions.
  const reloadTeams = () => {
    authFetch('/api/users/teams').then(r => r.ok ? r.json() : [])
      .then(data => setTeams(Array.isArray(data) ? data : [])).catch(() => {});
  };

  // One mount-time load that handles BOTH the team tree and the current
  // user's KPTs in parallel, then flips loading off exactly once. The
  // previous version kicked off the same /api/users/teams fetch from two
  // separate effects and updated state at different times, which made the
  // page briefly re-render between the two responses (the 'flicker').
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      authFetch('/api/users/teams').then(r => r.ok ? r.json() : []).catch(() => []),
      authFetch(`/api/users/${user?.id}/profile`).then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([teamsData, profile]) => {
      if (cancelled) return;
      const teamData = Array.isArray(teamsData) ? teamsData : [];
      setTeams(teamData);
      const myTeam = teamData.find(t => t.members?.some(m => m.user_id === user?.id));
      if (myTeam) {
        const expanded = new Set([myTeam.id]);
        let parent = teamData.find(t => t.id === myTeam.supervised_by);
        while (parent) { expanded.add(parent.id); parent = teamData.find(t => t.id === parent.supervised_by); }
        setDefaultExpanded(expanded);
      }
      if (profile?.kpts) setMyKpts(profile.kpts);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [user?.id]);

  const myTeamId = teams.find(t => t.members?.some(m => m.user_id === user?.id))?.id;

  // Get teams below the user's level (child teams only, not own team)
  const getManagedTeams = () => {
    if (!myTeamId) return [];
    const childIds = [];
    const findChildren = (parentId) => {
      teams.filter(t => t.supervised_by === parentId).forEach(t => {
        childIds.push(t.id);
        findChildren(t.id);
      });
    };
    findChildren(myTeamId);
    return teams.filter(t => childIds.includes(t.id));
  };

  const handleUserClick = (member) => {
    setShowProfileModal(true);
    setProfileLoading(true);
    setProfileUser(null);
    authFetch(`/api/users/${member.user_id}/profile`).then(r => r.ok ? r.json() : null)
      .then(data => { setProfileUser(data); setProfileLoading(false); })
      .catch(() => setProfileLoading(false));
  };


  const handleCreateUser = async (form) => {
    const res = await authFetch('/api/users/team', {
      method: 'POST',
      body: JSON.stringify(form)
    });
    if (res.ok) {
      showToast('User created and activation email sent');
      setShowCreateUserModal(false);
      setShowAddMemberModal(false);
      reloadTeams();
    } else {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.error || 'Failed to create user');
    }
  };

  // Auth mode drives whether the Add-Team-Member modal offers the
  // "pick from existing users" path (standalone/AD users are typically
  // already provisioned and just need to be slotted into a team).
  useEffect(() => {
    authFetch('/api/auth/mode').then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.mode) setAuthMode(data.mode); }).catch(() => {});
  }, []);

  const loadOrphans = () => {
    setOrphansLoading(true);
    return authFetch('/api/users/teams/orphans').then(r => r.ok ? r.json() : [])
      .then(data => { setOrphanUsers(Array.isArray(data) ? data : []); setOrphansLoading(false); })
      .catch(() => setOrphansLoading(false));
  };

  const openAddMember = () => {
    setShowAddMemberModal(true);
    setAddMemberStep('choose');
    setPickedOrphan(null);
    setOrphanHours({ mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 });
    setOrphanHoursTouched(false);
    authFetch('/api/users/next-employee-id').then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.employee_id) setNextEmployeeId(data.employee_id); }).catch(() => {});
    if (authMode !== 'local' || true) {
      // Always preload orphans — UI will only surface the option when
      // the auth mode is standalone or AD.
      loadOrphans();
    }
  };

  // Assign one of the picked orphan users to my team with the working
  // hours captured in the modal. Submit is gated on at least one hours
  // field having been touched (so the supervisor consciously sets it).
  const assignPickedOrphan = async () => {
    if (!pickedOrphan || !myTeamId) return;
    setOrphanAssigning(true);
    try {
      const res = await authFetch(`/api/users/teams/${myTeamId}/assign-existing`, {
        method: 'POST', body: JSON.stringify({ user_id: pickedOrphan.user_id, working_hours: orphanHours })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to add user');
      showToast(`${pickedOrphan.name} added to your team`);
      setShowAddMemberModal(false);
      reloadTeams();
    } catch (e) {
      showToast(e.message || 'Failed to add user', 'error');
    }
    setOrphanAssigning(false);
  };

  if (loading) return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="skeleton" style={{ width: 180, height: 28, marginBottom: 8 }} />
        <div className="skeleton" style={{ width: 200, height: 14 }} />
      </div>
      {[...Array(3)].map((_, i) => (
        <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 10, padding: 16, marginBottom: 8, border: '1px solid var(--border)' }}>
          <div className="skeleton" style={{ width: '50%', height: 16, marginBottom: 8 }} />
          <div className="skeleton" style={{ width: '30%', height: 12 }} />
        </div>
      ))}
    </div>
  );

  const managedTeams = getManagedTeams();

  return (
    <div>
      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            style={{ position: 'fixed', top: 20, right: 20, zIndex: 999, padding: '12px 20px', borderRadius: 10,
              background: toast.type === 'error' ? '#dc2626' : '#16a34a', color: '#fff', fontSize: 13, fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 'clamp(20px, 5vw, 28px)', fontWeight: 800, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>My Team</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-secondary)' }}>
            {myTeamId ? `You are in ${teams.find(t => t.id === myTeamId)?.name || 'a team'}` : 'You are not assigned to a team'}
            {' '}&middot; {teams.length} team{teams.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canManageUsers && (
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => { setShowOrphansView(v => { const next = !v; if (next) loadOrphans(); return next; }); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              <FiUsers size={14} /> {showOrphansView ? 'Show team chart' : 'Users not in a team'}
            </motion.button>
          )}
          {canManageUsers && (
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={openAddMember}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              <FiPlus size={14} /> Add Team Member
            </motion.button>
          )}
        </div>
      </motion.div>


      <div style={{ marginTop: 20 }}>
        {showOrphansView ? (
          <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Users not in a team</h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>{orphanUsers.length} user{orphanUsers.length !== 1 ? 's' : ''} — click any card to add them to your team</p>
              </div>
            </div>
            {orphansLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
            ) : orphanUsers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                <FiUsers size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                <div style={{ fontSize: 14, fontWeight: 600 }}>Everyone's in a team</div>
                <div style={{ fontSize: 12, marginTop: 2 }}>No active users are unassigned</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                {orphanUsers.map(o => {
                  const initials = (o.name || '?').split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();
                  return (
                    <motion.div key={o.user_id}
                      whileHover={{ y: -3, boxShadow: '0 8px 20px rgba(0,0,0,0.10)' }}
                      onClick={() => { if (!myTeamId) { showToast('Join a team first to add others', 'error'); return; } setShowAddMemberModal(true); setAddMemberStep('hours'); setPickedOrphan(o); setOrphanHours({ mon: parseFloat(o.working_hours_mon) || 0, tue: parseFloat(o.working_hours_tue) || 0, wed: parseFloat(o.working_hours_wed) || 0, thu: parseFloat(o.working_hours_thu) || 0, fri: parseFloat(o.working_hours_fri) || 0, sat: parseFloat(o.working_hours_sat) || 0, sun: parseFloat(o.working_hours_sun) || 0 }); setOrphanHoursTouched(false); }}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 16px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-input)', cursor: 'pointer' }}>
                      {o.avatar ? (
                        <img src={`/api/users/avatar/${o.avatar}`} alt="" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', marginBottom: 10 }} />
                      ) : (
                        <div style={{ width: 56, height: 56, borderRadius: '50%', background: o.avatar_color || '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 10 }}>{initials}</div>
                      )}
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center', marginBottom: 4, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.name}</div>
                      <div style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: o.auth_type === 'ad' ? 'rgba(99,102,241,0.1)' : 'rgba(16,185,129,0.1)', color: o.auth_type === 'ad' ? '#6366f1' : '#16a34a' }}>{(o.auth_type || 'local').toUpperCase()}</div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <TeamOrgChart teams={teams} currentUserId={user?.id} defaultExpanded={defaultExpanded} onUserClick={handleUserClick} hideTeamNames />
        )}
      </div>

      {/* Add Team Member Modal */}
      <AnimatePresence>
        {showAddMemberModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={() => setShowAddMemberModal(false)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 440, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)' }}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {addMemberStep === 'hours' ? `Working hours for ${pickedOrphan?.name || 'user'}` : 'Add Team Member'}
                </h3>
                <button onClick={() => setShowAddMemberModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>

              {addMemberStep === 'choose' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <motion.div whileHover={{ y: -2, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                    onClick={() => { setShowAddMemberModal(false); setShowCreateUserModal(true); }}
                    style={{ padding: 20, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-input)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(37,99,235,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <FiUserPlus size={20} color="#2563eb" />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>Create New User</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Add a new user to your team with an activation email</div>
                    </div>
                  </motion.div>
                  {(authMode === 'ad' || authMode === 'hybrid' || orphanUsers.length > 0) && (
                    <motion.div whileHover={{ y: -2, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                      onClick={() => { setAddMemberStep('pickExisting'); if (orphanUsers.length === 0) loadOrphans(); }}
                      style={{ padding: 20, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-input)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(99,102,241,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <FiUsers size={20} color="#6366f1" />
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>Add Existing User</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{authMode === 'local' ? 'Pick a user who is not yet in any team' : 'Pick a domain or local user not yet in a team'}</div>
                      </div>
                    </motion.div>
                  )}
                </div>
              )}

              {addMemberStep === 'pickExisting' && (
                <div>
                  {orphansLoading ? (
                    <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>Loading users…</div>
                  ) : orphanUsers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>No users are unassigned right now.</div>
                  ) : (
                    <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 4 }}>
                      {orphanUsers.map(o => (
                        <div key={o.user_id}
                          onClick={() => { setPickedOrphan(o); setOrphanHours({ mon: parseFloat(o.working_hours_mon) || 0, tue: parseFloat(o.working_hours_tue) || 0, wed: parseFloat(o.working_hours_wed) || 0, thu: parseFloat(o.working_hours_thu) || 0, fri: parseFloat(o.working_hours_fri) || 0, sat: parseFloat(o.working_hours_sat) || 0, sun: parseFloat(o.working_hours_sun) || 0 }); setOrphanHoursTouched(false); setAddMemberStep('hours'); }}
                          style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: o.avatar_color || '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                            {(o.name || '?').split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.email}</div>
                          </div>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: o.auth_type === 'ad' ? 'rgba(99,102,241,0.1)' : 'rgba(16,185,129,0.1)', color: o.auth_type === 'ad' ? '#6366f1' : '#16a34a' }}>{(o.auth_type || 'local').toUpperCase()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                    <button type="button" onClick={() => setAddMemberStep('choose')} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Back</button>
                  </div>
                </div>
              )}

              {addMemberStep === 'hours' && pickedOrphan && (
                <div>
                  <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-secondary)' }}>
                    Set this user's weekly working hours. Default is 0.0h on every day so capacity reports don't pre-allocate work to them.
                  </p>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                    {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((d, i) => (
                      <div key={d} style={{ flex: 1, textAlign: 'center' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase' }}>{['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][i]}</div>
                        <input
                          type="number" min="0" max="24" step="0.5"
                          value={orphanHours[d]}
                          onChange={e => { setOrphanHours(prev => ({ ...prev, [d]: e.target.value })); setOrphanHoursTouched(true); }}
                          style={{ width: '100%', padding: '8px 4px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, textAlign: 'center', background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16, textAlign: 'right' }}>
                    Total: <strong style={{ color: 'var(--text-primary)' }}>{Object.values(orphanHours).reduce((s, v) => s + (parseFloat(v) || 0), 0).toFixed(1)}h / week</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <button type="button" onClick={() => setAddMemberStep('pickExisting')} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Back</button>
                    <button type="button" onClick={assignPickedOrphan} disabled={!orphanHoursTouched || orphanAssigning}
                      style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: (!orphanHoursTouched || orphanAssigning) ? '#94a3b8' : 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: (!orphanHoursTouched || orphanAssigning) ? 'not-allowed' : 'pointer' }}>
                      {orphanAssigning ? 'Adding…' : 'Add to team'}
                    </button>
                  </div>
                </div>
              )}

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create User Modal */}
      <AnimatePresence>
        {showCreateUserModal && (
          <AddUserModal
            nextEmployeeId={nextEmployeeId}
            onClose={() => setShowCreateUserModal(false)}
            onBack={() => { setShowCreateUserModal(false); setShowAddMemberModal(true); setAddMemberStep('choose'); }}
            onSave={handleCreateUser}
            title="Create New Team Member"
            infoMessage="An activation email will be sent to the new user"
          />
        )}
      </AnimatePresence>

      {/* User Profile Modal */}
      <AnimatePresence>
        {showProfileModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={() => { setShowProfileModal(false); setProfileUser(null); setProfileLoading(false); setActionMsg(''); setTransferTeamId(''); }}>
            <motion.div initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '28px', width: '100%', maxWidth: 400, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)' }}>

              {profileLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{ width: 32, height: 32, border: '3px solid var(--border)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading profile...</div>
                </div>
              ) : profileUser ? (
                <>
                  {/* Header with gradient background */}
                  <div style={{ position: 'relative', background: `linear-gradient(135deg, ${profileUser.avatar_color || '#2563eb'}20, ${profileUser.avatar_color || '#2563eb'}08)`, borderRadius: '16px 16px 0 0', padding: '20px 24px 50px', margin: '-28px -28px 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {profileUser.disabled ? (
                          <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}>Disabled</span>
                        ) : (
                          <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: 'rgba(16,185,129,0.1)', color: '#10b981', border: '1px solid rgba(16,185,129,0.2)' }}>Active</span>
                        )}
                      </div>
                      <button onClick={() => { setShowProfileModal(false); setProfileUser(null); setProfileLoading(false); setActionMsg(''); setTransferTeamId(''); }}
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}><FiX size={20} /></button>
                    </div>
                  </div>

                  {/* Avatar + Name - overlapping the header */}
                  <div style={{ textAlign: 'center', marginTop: -40, marginBottom: 16, position: 'relative', zIndex: 1 }}>
                    {profileUser.avatar ? (
                      <img src={`/api/users/avatar/${profileUser.avatar}`} alt="" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', margin: '0 auto 10px', display: 'block', border: '4px solid var(--bg-card)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                    ) : (
                      <div style={{ width: 80, height: 80, borderRadius: '50%', background: profileUser.avatar_color || 'linear-gradient(135deg, #2563eb, #3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px', fontSize: 28, fontWeight: 700, color: '#fff', border: '4px solid var(--bg-card)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                        {getInitials(profileUser.name)}
                      </div>
                    )}
                    <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{profileUser.name}</div>
                    {profileUser.email && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
                        <FiMail size={12} /> {profileUser.email}
                      </div>
                    )}
                    {profileUser.phone_number && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center', fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                        <FiPhone size={12} /> {profileUser.phone_number}
                      </div>
                    )}
                    {/* Call / Email buttons */}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 10 }}>
                      {profileUser.phone_number && (
                        <a href={`tel:${profileUser.phone_number}`} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, background: 'rgba(16,185,129,0.1)', color: '#10b981', border: '1px solid rgba(16,185,129,0.2)', fontSize: 12, fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}>
                          <FiPhone size={13} /> Call
                        </a>
                      )}
                      {profileUser.email && (
                        <a href={`mailto:${profileUser.email}`} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, background: 'rgba(37,99,235,0.1)', color: '#2563eb', border: '1px solid rgba(37,99,235,0.2)', fontSize: 12, fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}>
                          <FiMail size={13} /> Email
                        </a>
                      )}
                    </div>
                  </div>


                  {/* Details grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: 16 }}>
                    {profileUser.employee_id && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                        <FiHash size={14} color="var(--text-muted)" />
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Employee ID</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{profileUser.employee_id}</div>
                        </div>
                      </div>
                    )}
                    {profileUser.dob && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                        <FiCalendar size={14} color="var(--text-muted)" />
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date of Birth</div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{formatDate(profileUser.dob)}</div>
                        </div>
                      </div>
                    )}
                    {profileUser.team_name && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                        <FiUsers size={14} color="var(--text-muted)" />
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Team</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 1 }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: profileUser.team_color || '#6b7280', flexShrink: 0 }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{profileUser.team_name}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {profileUser.supervisor_name && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                        <FiUsers size={14} color="var(--text-muted)" />
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Supervisor</div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{profileUser.supervisor_name}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* KPTs */}
                  {profileUser.kpts && profileUser.kpts.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        <FiShield size={13} color="var(--text-muted)" />
                        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Permissions (KPT)</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {profileUser.kpts.map(k => (
                          <span key={k.id} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: `${k.color}15`, color: k.color, border: `1px solid ${k.color}30` }}>
                            {k.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}


                  {/* Working hours — editable by team supervisors and admins */}
                  {canManageUsers && profileUser.id !== user?.id && (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 14 }}>
                      <WorkingHoursEditor userId={profileUser.id} initial={profileUser} authFetch={authFetch} showToast={showToast} />
                    </div>
                  )}

                  {/* Manage actions */}
                  {canManageUsers && profileUser.id !== user?.id && (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Team Management</div>
                      {actionMsg && <div style={{ fontSize: 12, color: '#16a34a', marginBottom: 8, fontWeight: 600 }}>{actionMsg}</div>}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <button onClick={async () => {
                          const endpoint = user?.permissions?.includes('admin_panel') ? `/api/users/${profileUser.id}/toggle-disable` : `/api/users/kpt/toggle-disable/${profileUser.id}`;
                          const res = await authFetch(endpoint, { method: 'PATCH' });
                          if (res.ok) {
                            const data = await res.json();
                            setActionMsg(data.disabled ? 'Account disabled' : 'Account enabled');
                            reloadTeams();
                            handleUserClick({ user_id: profileUser.id });
                          } else { const d = await res.json().catch(() => ({})); setActionMsg(d.error || 'Error'); }
                        }}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.04)', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          {profileUser.disabled ? 'Enable Account' : 'Disable Account'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Read-only view of this member's My Work (supervisor/admin only, not self) */}
                  {profileUser.id !== user?.id && (user?.permissions?.includes('supervisor_panel') || user?.permissions?.includes('admin_panel')) && (
                    <div style={{ marginTop: 20 }}>
                      <button onClick={() => { setShowProfileModal(false); navigate(`/my-work?user=${profileUser.id}`); }}
                        style={{ width: '100%', padding: '10px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <FiClipboard size={14} /> View their work orders
                      </button>
                    </div>
                  )}
                  <div style={{ marginTop: 12 }}>
                    <button onClick={() => { setShowProfileModal(false); setProfileUser(null); setProfileLoading(false); setActionMsg(''); setTransferTeamId(''); }}
                      style={{ width: '100%', padding: '10px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      Close
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>
                  Could not load profile
                  <div style={{ marginTop: 12 }}>
                    <button onClick={() => { setShowProfileModal(false); setProfileUser(null); setProfileLoading(false); }}
                      style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 }}>Close</button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
